import type { NarrativeUpdate } from '../NarrativeUpdatePanel/narrativeUpdatePanelFragment';
import { NarrativeUpdatePanel } from '../NarrativeUpdatePanel';
import DownSolid from '@simplywallst/ui-core/icons/DownSolid';
import { Button } from '@simplywallst/ui-core/next';
import { useEffect, useMemo, useState } from 'react';
import { useTracking } from 'react-tracking';

type Props = {
  updates: NarrativeUpdate[];
};

export const NarrativeUpdateCarousel = ({ updates }: Props) => {
  const sortedUpdates = useMemo(
    () =>
      [...updates].sort((a, b) => {
        if (!a.publishedAt || !b.publishedAt) return 0;
        return (
          new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime()
        );
      }),
    [updates]
  );

  const updatesLength = sortedUpdates.length;
  const lastIndex = updatesLength - 1;
  const [currentIndex, setCurrentIndex] = useState(lastIndex);
  const { trackEvent } = useTracking();

  const currentUpdate = sortedUpdates[currentIndex];

  useEffect(() => {
    if (currentUpdate) return;
    // Reset to the last update if the current update is not found (it could in theory happen if the cache refetches whilst the carousel is open)
    setCurrentIndex(lastIndex);
  }, [currentUpdate, lastIndex]);

  if (!currentUpdate) return null;

  return (
    <NarrativeUpdatePanel
      update={currentUpdate}
      title={currentIndex === lastIndex ? 'Last Update' : 'Shared on'}
      actions={
        <div className="grid grid-cols-[max-content_max-content] gap-x1">
          <Button
            variant="subtle"
            size="medium"
            aria-label="Previous Update"
            className="pl-x0_5"
            onClick={() => {
              setCurrentIndex((index) => Math.max(0, index - 1));
              trackEvent({
                action: 'click',
                subject: 'latest_update',
                modifier: 'previous',
              });
            }}
            isDisabled={currentIndex === 0}
          >
            <DownSolid className="rotate-90" /> Previous
          </Button>
          <Button
            variant="subtle"
            size="medium"
            aria-label="Next Update"
            className="pr-x0_5"
            onClick={() => {
              setCurrentIndex((index) => Math.min(lastIndex, index + 1));
              trackEvent({
                action: 'click',
                subject: 'latest_update',
                modifier: 'next',
              });
            }}
            isDisabled={currentIndex >= lastIndex}
          >
            Next <DownSolid className="-rotate-90" />
          </Button>
        </div>
      }
    />
  );
};
